import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Heart, MapPin, Book, Sparkles, Calendar, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { GriefMapExercise } from "./grief/GriefMapExercise";
import { ContinuingBondsJournal } from "./grief/ContinuingBondsJournal";
import { MeaningMakingExercise } from "./grief/MeaningMakingExercise";
import { MemoryProcessingExercise } from "./grief/MemoryProcessingExercise";
import { AnniversaryPlanningExercise } from "./grief/AnniversaryPlanningExercise";
import { SavedGriefMaps } from "./grief/SavedGriefMaps";
import { EditableText } from "@/hooks/useEditableContent";
interface GriefToolsProps {
  user: any;
}
export const GriefTools = ({
  user
}: GriefToolsProps) => {
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [entries, setEntries] = useState<Record<string, string>>({});
  const tools = [{
    id: 'grief-map',
    name: 'Interactive Grief Mapping',
    icon: MapPin,
    color: 'text-primary',
    bgColor: 'bg-primary/10',
    description: 'Visualise your grief journey across different loss types',
    timeframe: '15-20 min',
    category: 'Processing'
  }, {
    id: 'continuing-bonds',
    name: 'Continuing Bonds Journal',
    icon: Heart,
    color: 'text-soft-accent',
    bgColor: 'bg-soft-accent/10',
    description: 'Maintain healthy connection with what/who was lost',
    timeframe: '10-15 min',
    category: 'Connection'
  }, {
    id: 'meaning-making',
    name: 'Meaning Reconstruction',
    icon: Book,
    color: 'text-secondary',
    bgColor: 'bg-secondary/10',
    description: 'Find meaning and growth through loss',
    timeframe: '15-20 min',
    category: 'Integration'
  }, {
    id: 'memory-processing',
    name: 'Memory Processing',
    icon: Sparkles,
    color: 'text-primary',
    bgColor: 'bg-primary/10',
    description: 'Process memories in a structured, gentle way',
    timeframe: '10-15 min',
    category: 'Processing'
  }, {
    id: 'anniversary-planning',
    name: 'Anniversary Planning',
    icon: Calendar,
    color: 'text-soft-accent',
    bgColor: 'bg-soft-accent/10',
    description: 'Prepare for difficult dates and anniversaries',
    timeframe: '10-15 min',
    category: 'Preparation'
  }, {
    id: 'grief-letter',
    name: 'Letter Writing',
    icon: MessageCircle,
    color: 'text-secondary',
    bgColor: 'bg-secondary/10',
    description: 'Write to your loved one or to the loss itself',
    timeframe: '15-30 min',
    category: 'Expression'
  }];

  // Render active tool
  if (activeTool === 'grief-map') {
    return <GriefMapExercise onBack={() => setActiveTool(null)} userId={user.id} />;
  }
  if (activeTool === 'view-maps') {
    return <SavedGriefMaps onBack={() => setActiveTool(null)} userId={user.id} />;
  }
  if (activeTool === 'continuing-bonds') {
    return <ContinuingBondsJournal onBack={() => setActiveTool(null)} />;
  }
  if (activeTool === 'meaning-making') {
    return <MeaningMakingExercise onBack={() => setActiveTool(null)} />;
  }
  if (activeTool === 'memory-processing') {
    return <MemoryProcessingExercise onBack={() => setActiveTool(null)} />;
  }
  if (activeTool === 'anniversary-planning') {
    return <AnniversaryPlanningExercise onBack={() => setActiveTool(null)} />;
  }
  if (activeTool === 'grief-letter') {
    return <div className="space-y-6 animate-fade-in">
        <Button variant="outline" onClick={() => setActiveTool(null)} className="mb-4">
          ← Back to Grief Tools
        </Button>

        <Card className="bg-gradient-therapeutic border-primary/20">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-soft-accent/10">
                <MessageCircle className="h-6 w-6 text-soft-accent" />
              </div>
              <div>
                <CardTitle className="text-2xl font-light">Letter Writing</CardTitle>
                <CardDescription>
                  Express what you haven't been able to say
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Instructions */}
            <Card className="bg-gradient-calm border-border/50">
              <CardContent className="py-4 space-y-3">
                <p className="text-sm font-medium">Gentle Guidance:</p>
                <ul className="text-sm space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>Write as if they can hear you. Express whatever you wish you could say.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>Include gratitude, anger, longing, memories - all feelings are welcome.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>You don't need to send this to anyone. This is for YOU.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>There's no right or wrong way to do this.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Writing Area */}
            <div className="space-y-3">
              <label className="text-base font-medium">Your letter:</label>
              <Textarea value={entries['grief-letter'] || ''} onChange={e => setEntries({
              ...entries,
              'grief-letter': e.target.value
            })} placeholder="Dear..." className="min-h-[300px] bg-background/80 leading-relaxed text-base" />
              <p className="text-sm text-right">
                {entries['grief-letter']?.length || 0} characters
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1">
                Save as Draft
              </Button>
              <Button className="flex-1" onClick={() => toast.success('Letter saved securely')}>
                Save & Keep Private
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Science Behind Letter Writing */}
        <Card className="bg-gradient-calm border-border/50">
          <CardContent className="py-4">
            <div className="space-y-3">
              <p className="text-base font-medium">The Science: Why Letter Writing Is So Powerful</p>
              <div className="text-sm leading-relaxed space-y-2">
                <p>
                  <span className="font-medium text-foreground">Expressive Writing Research</span> (Pennebaker & Beall) shows that writing about emotional 
                  experiences activates both hemispheres of the brain-the emotional right side and the language/meaning-making left side. 
                  This integration helps process trauma and loss at a deeper level.
                </p>
                <p>
                  <span className="font-medium text-foreground">What happens in your brain:</span> Writing creates narrative coherence-your brain can't 
                  hold onto fragmented emotional experiences indefinitely. By putting feelings into words, you're literally organising neural pathways, 
                  which reduces the intensity of intrusive thoughts and emotional flooding.
                </p>
                <p>
                  <span className="font-medium text-foreground">Benefits you may experience:</span> Reduced blood pressure and cortisol levels, improved 
                  immune function, better sleep, decreased depression symptoms, and most importantly-a sense of completing what was left unsaid. 
                  You can write multiple letters as your grief evolves-there's no "one and done."
                </p>
                <p className="text-foreground">
                  This isn't about "moving on"-it's about continuing your bond in a new way that honours both them and your healing.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>;
  }
  return <div className="space-y-6">
      {/* View Saved Maps Button - More Prominent */}
      <Card className="bg-gradient-to-r from-primary/10 to-soft-accent/10 border-primary/30">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="font-medium text-foreground">Track Your Grief Journey Over Time</p>
              <p className="text-sm text-muted-foreground">View and compare your saved grief maps to see how your experience is shifting</p>
            </div>
            <Button variant="default" onClick={() => setActiveTool('view-maps')} className="gap-2 ml-4">
              <MapPin className="h-4 w-4" />
              View Saved Maps
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Header */}
      <div className="text-center">
        <h3 className="text-2xl font-light text-foreground mb-2">
          <EditableText contentKey="grief.header.title" defaultContent="Grief & Loss Support" />
        </h3>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          <EditableText contentKey="grief.header.description" defaultContent="Evidence-based tools to support you through death, relationship loss, health changes, or any significant loss" />
        </p>
      </div>

      {/* Psycho-Education Card */}
      <Card className="bg-gradient-therapeutic border-primary/20">
        <CardContent className="py-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Heart className="h-5 w-5 text-primary" />
              <p className="text-lg font-medium">
                <EditableText contentKey="grief.education.title" defaultContent="Understanding Grief: Why These Tools Can Help" />
              </p>
            </div>
            
            <div className="space-y-3 text-base leading-relaxed">
              <p>
                <EditableText contentKey="grief.education.paragraph1" defaultContent="Grief isn't something you get over or move through in stages. It's deeply personal, and there's no right way to grieve. Modern research shows grief is an oscillation between confronting your loss and adapting to life's changes-what researchers call the Dual Process Model." />
              </p>
              
              <p>
                <EditableText contentKey="grief.education.paragraph2" defaultContent="Why loss hurts so much: Grief activates the same brain regions as physical pain. This is why your loss literally hurts. Your brain is gently working to reconfigure millions of neural connections, learning to hold your loved one in a new way whilst adapting to their physical absence." />
              </p>
              
              <p>
                <EditableText contentKey="grief.education.paragraph3" defaultContent="How these tools support you: Writing, mapping, and gentle reflection help your thinking brain support your emotional brain. This doesn't suppress grief-it helps you process it safely, at your own pace. Research shows expressive writing about loss can reduce intrusive thoughts, improve wellbeing, and help you find meaning." />
              </p>
              
              <p>
                <EditableText contentKey="grief.education.paragraph4" defaultContent="You don't have to let go: Continuing Bonds theory shows that maintaining a connection to your loved one is healthy and healing-not something to release. These tools honour that ongoing bond." />
              </p>
            </div>
            
            <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-border/50">
              <Badge variant="secondary" className="text-xs">All feelings are valid</Badge>
              <Badge variant="secondary" className="text-xs">No timeline expectations</Badge>
              <Badge variant="secondary" className="text-xs">Take what helps</Badge>
              <Badge variant="secondary" className="text-xs">Leave what doesn't</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Beyond Grief - Sensitive Afterlife Experiences Section */}
      <Card className="bg-gradient-to-br from-primary/5 to-soft-accent/5 border-primary/20">
        <CardContent className="py-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <p className="text-lg font-medium">
                <EditableText contentKey="grief.afterlife.title" defaultContent="Beyond Grief: Signs, Experiences & Connection" />
              </p>
            </div>
            
            <div className="space-y-3 text-base leading-relaxed">
              <p>
                <EditableText contentKey="grief.afterlife.paragraph1" defaultContent="Research shows that 60-80% of bereaved people report sensing the presence of their loved ones or receiving signs after death. These experiences-whether dreams, synchronicities, or felt presences-are normal, common, and can be deeply healing." />
              </p>
              
              <p>
                <EditableText contentKey="grief.afterlife.paragraph2" defaultContent="Studies indicate that people who find meaning in these experiences report better emotional wellbeing, reduced grief intensity, and a greater sense of ongoing connection. The Continuing Bonds model recognises that maintaining this connection can be part of healthy grieving." />
              </p>
              
              <p>
                <EditableText contentKey="grief.afterlife.paragraph3" defaultContent="If you feel drawn to explore this dimension of your grief, our Telempathy Teachings® programme includes gentle practices for:" />
              </p>
              
              <ul className="text-base space-y-2 ml-4">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>
                    <EditableText contentKey="grief.afterlife.practice1" defaultContent="Mediumship development to strengthen your connection" />
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>
                    <EditableText contentKey="grief.afterlife.practice2" defaultContent="Dream journalling to understand messages during sleep" />
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>
                    <EditableText contentKey="grief.afterlife.practice3" defaultContent="Synchronicity tracking to recognise meaningful signs" />
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>
                    <EditableText contentKey="grief.afterlife.practice4" defaultContent="Spirit connection practices in a safe, structured way" />
                  </span>
                </li>
              </ul>
              
              <p className="text-sm pt-2 border-t border-border/30 mt-4">
                <EditableText contentKey="grief.afterlife.disclaimer" defaultContent="This path isn't for everyone, and that's completely okay. Take only what resonates, leave what doesn't. There's no pressure-just gentle invitation if this feels right for you." />
              </p>
            </div>
            
            <Button onClick={() => window.location.href = '/telempathy-tools'} className="w-full mt-4" variant="default">
              <Sparkles className="h-4 w-4 mr-2" />
              <EditableText contentKey="grief.afterlife.button" defaultContent="Explore Beyond Grief" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tools Grid */}
      <div className="grid gap-4 md:grid-cols-2">
        {tools.map(tool => {
        const Icon = tool.icon;
        return <Card key={tool.id} className="hover:shadow-therapy transition-all duration-300 cursor-pointer border-border/50" onClick={() => setActiveTool(tool.id)}>
              <CardHeader>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`p-2 rounded-lg ${tool.bgColor}`}>
                      <Icon className={`h-5 w-5 ${tool.color}`} />
                    </div>
                     <div>
                      <CardTitle className="text-lg mb-1 font-aileron">{tool.name}</CardTitle>
                      <CardDescription className="text-sm">{tool.description}</CardDescription>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Badge variant="outline" className="text-xs font-aileron">{tool.timeframe}</Badge>
                  <Badge variant="secondary" className="text-xs font-aileron">{tool.category}</Badge>
                </div>
              </CardHeader>
            </Card>;
      })}
      </div>

      {/* Support Note */}
      <Card className="bg-gradient-to-br from-primary/10 to-soft-accent/10 border-primary/30">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Heart className="h-5 w-5 text-primary mt-0.5" />
            <div className="space-y-2">
              <p className="text-base font-medium text-foreground font-aileron">
                <EditableText contentKey="grief.support.title" defaultContent="You're Not Alone in This" />
              </p>
              <p className="text-base text-foreground leading-relaxed">
                <EditableText contentKey="grief.support.paragraph1" defaultContent="Whatever you've lost-a child, a partner, a parent, a friend, your health, a dream-your grief is valid and deserves tenderness. These tools are here to support you, but they're not a replacement for professional grief counselling or bereavement support if you need it. Reaching out for help is an act of courage, not weakness." />
              </p>
              <p className="text-base text-foreground leading-relaxed">
                <EditableText contentKey="grief.support.paragraph2" defaultContent="Some days will feel impossible. Other days, you might find small moments of peace. Both are part of your unique journey. There's no timeline, no right way to grieve. Be gentle with yourself." />
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>;
};